import { env } from '../config/env';

export interface LoginIn {
  email: string;
  password: string;
  app_name: string;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type?: string;
}

export interface AccessTokenOut {
  access_token: string;
  token_type?: string;
}

function joinUrl(path: string): string {
  const base = env.authApiUrl.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

function buildHref(path: string): string {
  const url = new URL(joinUrl(path), window.location.origin);
  return env.authApiUrl.startsWith('http') ? url.href : `${url.pathname}${url.search}`;
}

async function parseJson<T>(res: Response): Promise<T> {
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const detail =
      typeof body === 'object' && body && 'detail' in body
        ? (body as { detail: unknown }).detail
        : null;
    const message =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail
              .map((d) =>
                d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : String(d),
              )
              .join('; ')
          : `HTTP ${res.status} ${res.statusText}`;
    throw new AuthApiError(message, res.status, body);
  }
  return body as T;
}

/** Auth API error with HTTP status (404 = unknown app_name, 401 = bad refresh, …). */
export class AuthApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown = null,
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

async function authPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(buildHref(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseJson<T>(res);
}

export async function postLogin(payload: LoginIn): Promise<TokenPair> {
  return authPost<TokenPair>('/auth/login', payload);
}

/** Rotate refresh and issue a new access token. Body: `{ refresh_token }`. */
export async function postRefresh(refresh_token: string): Promise<TokenPair> {
  return authPost<TokenPair>('/auth/refresh', { refresh_token });
}

/** Exchange portal refresh for access JWT with target app aud (/auth/token). */
export async function postTokenExchange(
  refresh_token: string,
  app_name: string,
): Promise<AccessTokenOut> {
  return authPost<AccessTokenOut>('/auth/token', { refresh_token, app_name });
}

export async function postLogout(refresh_token: string): Promise<void> {
  const res = await fetch(buildHref('/auth/logout'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token }),
  });
  if (!res.ok && res.status !== 401) {
    await parseJson(res);
  }
}
